import React, { useEffect, useState } from "react";
import Header from "../header/Header";
import { useQuery } from "@tanstack/react-query";
import { Button, Col, Form, Input, Layout, Menu, Row, Upload, Modal, Switch, Space, Card, Alert } from "antd";
import { useMyContext } from "../../context/MyContext";
import { api, API_BASE_URL } from "../../config/api";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import DashboardLayout from "../layout";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword as firebaseUpdatePassword } from "firebase/auth";
import { db, storage, auth } from "../../config/firebase";
import { doc, getDoc, updateDoc, increment, deleteField } from "firebase/firestore";
import { LockOutlined, SafetyOutlined, MailOutlined } from '@ant-design/icons';
import { getAllAdminUsers } from "../../lib/user";

const { Content, Sider } = Layout;

const Profile = () => {

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPasswordData, setNewPasswordData] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [passwordForm] = Form.useForm();
  const [pendingProfileData, setPendingProfileData] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  // 2FA States
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [is2FAModalVisible, setIs2FAModalVisible] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [is2FALoading, setIs2FALoading] = useState(false);

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const { user, updateUserProfile, setUser } = useMyContext();
  const [form] = Form.useForm();
  useEffect(() => {
    form.setFieldsValue({ ...user });
  }, [user]);

  const [active, setActive] = useState("Profile");
  const handleMenuSelect = ({ key }) => {
    setActive(key);
  };

  const [loading, setLoading] = useState(false);
  const items1 = ["Profile", "Password", "2 Factor Authentication"].map((key) => ({
    key,
    label: key,
  }));

  const updateUserPassword = async (newPassword) => {
    if (!user) return;
    setNewPasswordData(newPassword);
    setConfirmAction("updatePassword");
    setIsModalVisible(true);
  };

  const handleFormFinish = (values) => {
    if (!user) return;
    setPendingProfileData(values);
    setConfirmAction("updateProfile");
    setIsModalVisible(true);
  };

  const handleModalConfirm = async () => {
    if (!currentPassword) {
      toast.error("Please enter your current password or current temporary password");
      return;
    }

    try {
      setConfirmLoading(true);

      let tempPasswordVerified = false;

      // First, reauthenticate the user with real password
      try {
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(auth.currentUser, credential);
      } catch (reauthError) {
        if (reauthError.code === 'auth/wrong-password' || reauthError.code === 'auth/invalid-credential') {
          const tempPasswordResponse = await api.post("/auth/verify-temp-password", { password: currentPassword });
          if (tempPasswordResponse?.data?.ok) {
            tempPasswordVerified = true;
          } else {
            throw reauthError;
          }
        } else {
          throw reauthError;
        }
      }
      if (confirmAction === "updateProfile") {
        await updateProfile(pendingProfileData);
        setPendingProfileData(null);
      } else if (confirmAction === "updatePassword") {
        if (newPasswordData === currentPassword) {
          toast.error("New password cannot be the same as the current password");
          setConfirmLoading(false);
          return;
        }
        if (!tempPasswordVerified) {
          await firebaseUpdatePassword(auth.currentUser, newPasswordData);
        }
        await api.put(`/users/${user?.uid}/secure`, { password: newPasswordData });
        setCurrentPassword('');
        setNewPasswordData(null);
        passwordForm.resetFields();
        toast.success("Password changed successfully!");
      }
      setIsModalVisible(false);
    } catch (error) {
      console.error("Error updating password:", error);

      // Handle specific Firebase auth errors
      if (error.code === 'auth/wrong-password') {
        toast.error("Current password or temporary password is incorrect");
      } else if (error.code === 'auth/weak-password') {
        toast.error("New password is too weak. It must be at least 6 characters long.");
      } else if (error.code === 'auth/invalid-credential') {
        toast.error("Enter correct current password or temporary password.");
      } else {
        toast.error(error?.message || "Failed to update password. Please try again.");
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setCurrentPassword('');
    setNewPasswordData(null);
    passwordForm.resetFields();
  };

  const handleFormFinishFailed = (errorInfo) => {
    // console.log("Failed:", errorInfo);
    errorInfo.errorFields.forEach((error) => {
      toast.error(`${error.name}: ${error.errors[0]}`);
    });
  };

  const queryClient = useQueryClient();
  const { data: oldUserData, isLoading: isUserLoading } = useQuery(
    ["users", user?.uid],
    async () => {
      const userDocRef = doc(db, "users", user?.uid);
      const userSnap = await getDoc(userDocRef);
      return userSnap.exists() ? userSnap.data() : null;
    },
    {
      enabled: !!user?.uid, // Run query only if user is available
    }
  );
  // Helper function to compare old and new data
  const getChangedFields = (oldData, newData) => {
    const changes = {};
    const fieldsToCompare = ['country', 'city', 'postalCode', 'vatNumber', 'address', 'profile'];
    fieldsToCompare.forEach(field => {
      if (oldData[field] !== newData[field]) {
        changes[field] = {
          old: oldData[field],
          new: newData[field]
        };
      }
    });
    return changes;
  };

  const updateProfile = async (userData) => {
    let newData = { ...user, ...userData };
    try {
      setLoading(true);
      const admins = await getAllAdminUsers();
      const adminEmails = admins.map((admin) => admin.email);
      const changedFields = getChangedFields(oldUserData, newData);
      if (Object.keys(changedFields).length === 0) {
        toast.info("No changes detected in the profile.");
        return;
      }
      await api.put(`/users/${user?.uid}`, newData);
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, {
        profileUpdateCount: increment(1),
        lastProfileUpdate: new Date().toISOString()
      });
      // Send notification to admin
      await api.post("/notifications", {
        role: "admin",
        title: "Profile Update Alert",
        message: `User ${user.name} (${user.email}) has updated their profile.`,
        type: "new-quote",
        link: `/clients`,
        refId: user.uid,
      });
      // Send email to admin
      await api.post(`${API_BASE_URL}/mail/update-profile-email`, {
        oldUserData: oldUserData,
        newData: newData,
        adminEmails
      });
      updateUserProfile(newData);
      queryClient.invalidateQueries(["users"]);
      toast.success("User data updated successfully!");
      window.location.reload();
    } catch (error) {
      toast.error("Error updating data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const [progress, setProgress] = useState(0);
  const handleFileUpload = async (file) => {
    if (!file) {
      console.error("No file selected.");
      return;
    }
    setLoading(true);
    try {
      const storageRef = ref(storage, `profile/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progressPercentage =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(progressPercentage);
        },
        (error) => {
          setLoading(false);
          console.error("Error uploading file:", error);
          toast.error("Error uploading file: " + error.message);
        },
        async () => {
          const downloadURL = await getDownloadURL(storageRef);
          const userDocRef = doc(db, "users", user.uid);
          await updateDoc(userDocRef, {
            profile: downloadURL,
          });
          updateUserProfile({ profile: downloadURL });
          setLoading(false);
          setProgress(100);
          toast.success("Uploaded successfully!");
        }
      );
    } catch (error) {
      setLoading(false);
      console.error("Error uploading file:", error);
      toast.error("Error uploading file: " + error.message);
    }
  };

  const handleRemove = async (file) => {
    try {
      const storageRef = ref(storage, `profile/${file.name}`);
      await deleteObject(storageRef);
      toast.success(`${file.name} deleted successfully`);
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error(`Error deleting file: ${error.message}`);
    }
  };

  // 2FA Handler Functions
  const handleEnable2FA = async () => {
    try {
      setLoading(true);
      await api.post('/send-otp', { userId: user.uid, email: user.email });
      toast.success("OTP has been sent to your registered email");
      setIs2FAModalVisible(true);
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const handleVerifyOTP = async () => {
    const otpString = otp.join('');

    if (otpString.length !== 6) {
      toast.error("Please enter a valid OTP.");
      return;
    }

    try {
      setIs2FALoading(true);
      await api.post('/verify-otp', { otp: otpString, userId: user.uid });
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, { is2faEnabled: true });
      updateUserProfile({ is2faEnabled: true });

      setIs2FAModalVisible(false);
      setOtp(['', '', '', '', '', '']);
      toast.success("2FA enabled successfully!");
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error('Failed to verify OTP. Please try again.');
    } finally {
      setIs2FALoading(false);
    }
  };


  const handleDisable2FA = async () => {
    try {
      setLoading(true);
      // Placeholder: Disable 2FA
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, { is2faEnabled: false });
      updateUserProfile({ is2faEnabled: false });
      setTwoFAEnabled(false);
      toast.success("Two-Factor Authentication disabled");
    } catch (error) {
      toast.error("Failed to disable 2FA. Please try again.");
      console.error("Error disabling 2FA:", error);
    } finally {
      setLoading(false);
    }
  };


  const handleOTPModalCancel = () => {
    setIs2FAModalVisible(false);
    setOtp(['', '', '', '', '', '']);
  };

  const handleInputChange = (value, index) => {
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  }

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').slice(0, 6);
    const newOtp = [...otp];

    for (let i = 0; i < pasted.length; i++) {
      if (/^\d$/.test(pasted[i])) {
        newOtp[i] = pasted[i];

        const nextInput = document.getElementById(`otp-${i + 1}`);
        if (nextInput) {
          nextInput.focus();
        }
      }
    }

    setOtp(newOtp);
  }

  useEffect(() => {
    if (user && user.is2faEnabled) {
      setTwoFAEnabled(true);
    }
  }, [user]);

  const isClient = user?.role === 'user';
  return (
    <DashboardLayout>
      <div className="w-full min-h-screen border">
        <Header title="Profile" />
        <div className="p-4">
          <Layout className="bg-cus-gray">
            <Content style={{ padding: "0 48px" }}>
              <Layout className="bg-cus-gray" style={{ padding: "24px 0" }}>
                <Sider width={250}>
                  <Menu
                    mode="inline"
                    defaultSelectedKeys={["Profile"]}
                    defaultOpenKeys={["Profile"]}
                    selectedKeys={[active]}
                    style={{ height: "100%" }}
                    onSelect={handleMenuSelect}
                    items={items1}
                  />
                </Sider>
                <Content className="bg-cus-gray" style={{ padding: "0 24px", minHeight: 280 }}>
                  {active === "Profile" && (
                    <div className="p-4 mx-auto min-h-[80vh] bg-white border rounded-md h-[80vh] overflow-y-auto">
                      <Form
                        layout="vertical"
                        form={form}
                        onFinish={handleFormFinish}
                        onFinishFailed={handleFormFinishFailed}
                      >
                        <Row gutter={12}>
                          <div className="flex w-full gap-5 justify-center items-center">
                            <Form.Item
                              name="file"
                              valuePropName="fileList"
                              getValueFromEvent={normFile}
                            >
                              {/* <Upload.Dragger
                                beforeUpload={(file) => false}
                                name="files"
                                maxCount={1}
                                onChange={(e) => {
                                  handleFileUpload(e.file.originFileObj);
                                }}
                                onDrop={(e) =>
                                  handleFileUpload(e.dataTransfer.files[0])
                                }
                                onRemove={(file) => handleRemove(file.originFileObj)}
                              >
                                <p className="ant-upload-drag-icon">
                                  <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">
                                  Supporting Documents (E.G. Commercial Invoice, Packing List, Datasheets)
                                </p>
                              </Upload.Dragger> */}
                            </Form.Item>
                            <div className="hidden md:inline mx-auto col-span-1 p-3">
                              <h1 className="text-[#000] font-semibold">
                                Profile Update
                              </h1>
                              <div className="h-[154px] rounded-[10px] mt-3">
                                {user?.profile ? (
                                  <img
                                    src={user?.profile}
                                    alt=""
                                    className="h-40 w-40 border rounded-xl object-cover"
                                  />
                                ) : (
                                  <img src="/pro.svg" alt="" />
                                )}
                              </div>
                              <Upload
                                beforeUpload={(file) => {
                                  const isImage =
                                    file.type === "image/png" ||
                                    file.type === "image/jpeg" ||
                                    file.type === "image/jpg";
                                  if (!isImage) {
                                    toast.error(
                                      "You can only upload PNG, JPG, or JPEG files!"
                                    );
                                    return Upload.LIST_IGNORE;
                                  }
                                  const isLt2M = file.size / 1024 / 1024 < 2;
                                  if (!isLt2M) {
                                    toast.error(
                                      "Image must be smaller than 2MB!"
                                    );
                                    return Upload.LIST_IGNORE;
                                  }
                                  handleFileUpload(file);
                                  return false;
                                }}
                                showUploadList={false}
                                accept=".png,.jpg,.jpeg"
                              >
                                <Button className="active:opacity-70 btnBg mt-8 text-white px-5 rounded-[5px]">
                                  Change Profile
                                </Button>
                              </Upload>
                              {/* <Upload
                                beforeUpload={(file) => {
                                  handleFileUpload(file);
                                  return false;
                                }}
                                showUploadList={false}
                              >
                                <Button className="active:opacity-70 btnBg mt-8 text-white px-5  rounded-[5px]">
                                  Change Profile
                                </Button>
                              </Upload> */}
                            </div>
                          </div>
                          <Col md={12}>
                            <Form.Item
                              name="name"
                              label="Name"
                              rules={[
                                {
                                  required: true,
                                  message: "Name is required!",
                                },
                              ]}
                            >
                              <Input disabled={isClient} />
                            </Form.Item>
                          </Col>
                          <Col md={12}>
                            <Form.Item
                              name="email"
                              label="Email"
                              rules={[
                                {
                                  required: true,
                                  message: "Email is required!",
                                },
                                {
                                  type: "email",
                                  message:
                                    "Please enter a valid email address!",
                                },
                              ]}
                            >
                              <Input disabled={isClient} />
                            </Form.Item>
                          </Col>
                          <Col md={12}>
                            <Form.Item name="address" label="Address"
                              rules={[
                                {
                                  required: true,
                                  message: "Address is required!",
                                },
                              ]}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col md={12}>
                            <Form.Item name="city" label="City"
                              rules={[
                                {
                                  required: true,
                                  message: "City is required!",
                                },
                              ]}>
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col md={12}>
                            <Form.Item
                              name="postalCode"
                              label="Postal Code"
                              rules={[
                                {
                                  required: true,
                                  message: "Postal Code is required!",
                                },
                              ]}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col md={12}>
                            <Form.Item name="country" label="Country"
                              rules={[
                                {
                                  required: true,
                                  message: "Country is required!",
                                },
                              ]}>
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col md={12}>
                            <Form.Item name="vatNumber" label="Vat Number">
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Form.Item>
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                          >
                            Update Profile
                          </Button>
                        </Form.Item>
                      </Form>
                    </div>
                  )}
                  {active === "Password" && (
                    <div className="p-4 mx-auto bg-white border rounded-md h-[80vh] overflow-y-auto">
                      <Form
                        layout="vertical"
                        form={passwordForm}
                        onFinish={(values) => {
                          updateUserPassword(values.password);
                        }}
                      >
                        <Row gutter={12}>
                          <Col md={12}>
                            <Form.Item
                              name="password"
                              label="New Password"
                              rules={[
                                {
                                  required: true,
                                  message: "Please input your new password!",
                                },
                                {
                                  min: 6,
                                  message: "Password must be at least 6 characters long",
                                }
                              ]}
                              hasFeedback
                            >
                              <Input.Password />
                            </Form.Item>
                          </Col>
                          <Col md={12}>
                            <Form.Item
                              name="confirm"
                              label="Confirm New Password"
                              dependencies={["password"]}
                              hasFeedback
                              rules={[
                                {
                                  required: true,
                                  message: "Please confirm your password!",
                                },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    if (!value || getFieldValue("password") === value) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(
                                      new Error(
                                        "The passwords that you entered do not match!"
                                      )
                                    );
                                  },
                                }),
                              ]}
                            >
                              <Input.Password />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Form.Item>
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                          >
                            Save
                          </Button>
                        </Form.Item>
                      </Form>
                    </div>
                  )}
                  {active === '2 Factor Authentication' && (
                    <div className="bg-white p-8 h-[80vh] overflow-y-auto">
                      <div className="border-b border-gray-200 pb-6 mb-6">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                          Two-factor authentication
                        </h2>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Enabling two-factor authentication provides an added layer of security. Once enabled,
                          you'll need to enter a 6-digit code sent to your email when logging in.
                        </p>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-gray-600">
                            {twoFAEnabled ? (
                              <span className="text-green-600 font-medium">2FA Enabled</span>
                            ) : (
                              <span className="text-gray-500">2FA is currently disabled</span>
                            )}
                          </p>
                        </div>

                        <button
                          onClick={twoFAEnabled ? handleDisable2FA : handleEnable2FA}
                          disabled={loading}
                          className={`px-5 py-2 ${twoFAEnabled ? 'bg-red-500 hover:bg-red-600' : 'btnBg'} text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {loading ? 'Loading...' : twoFAEnabled ? 'Disable 2FA' : 'Enable 2FA'}
                        </button>
                      </div>
                    </div>
                  )}
                </Content>
              </Layout>
            </Content>
          </Layout>
        </div>
        <Modal
          title={
            <div className="flex items-center gap-2">
              <LockOutlined className="text-blue-500" />
              <span>Confirm Current Password</span>
            </div>
          }
          open={isModalVisible}
          onOk={handleModalConfirm}
          onCancel={handleModalCancel}
          confirmLoading={confirmLoading}
          okText="Confirm"
          cancelText="Cancel"
          okButtonProps={{
            className: 'bg-blue-500 hover:bg-blue-600'
          }}
          destroyOnClose={true}
        >
          <div className="py-4">
            <p className="mb-4 text-gray-600">
              For security reasons, please enter your current password or current temporary password to continue.
            </p>
            <Form.Item
              name="currentPassword"
              className="mb-0"
              rules={[
                {
                  required: true,
                  message: "Please enter your current password or current temporary password!"
                }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="text-gray-400" />}
                placeholder="Enter your current password or temp password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoFocus
              />
            </Form.Item>
          </div>
        </Modal>

        {/* 2FA OTP Verification Modal */}
        <Modal
          title={
            <div className="flex items-center gap-2">
              <SafetyOutlined className="text-blue-500" />
              <span>Verify Email</span>
            </div>
          }
          open={is2FAModalVisible}
          onOk={handleVerifyOTP}
          onCancel={handleOTPModalCancel}
          confirmLoading={is2FALoading}
          okText="Verify"
          cancelText="Cancel"
          okButtonProps={{
            className: 'bg-blue-500 hover:bg-blue-600'
          }}
          destroyOnClose={true}
        >
          <div className="py-4">
            <p className="mb-6 text-gray-600">
              We've sent a 6-digit OTP to your registered email address. Please enter it below to verify your email and enable Two-Factor Authentication.
            </p>
            <div
              className="mb-0 flex justify-center gap-2"
            >
              {otp.map((digit, idx) => (
                <input
                  type="text"
                  name="otp"
                  id={`otp-${idx}`}
                  maxLength={1}
                  autoFocus={idx === 0}
                  value={digit}
                  onChange={(e) => handleInputChange(e.target.value, idx)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  onPaste={handlePaste}
                  className='w-14 h-14 text-center rounded-md text-2xl border-2 border-[#00000026] outline-none active:border-cus-blue focus:border-cus-blue'
                />
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Didn't receive the code? Check your spam folder or request a new one.
            </p>
          </div>
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
